import { useEffect } from "react";
import {
  clearVisualViewportFreeze,
  KEYBOARD_CLOSE_OFFSET_PX,
  KEYBOARD_OPEN_OFFSET_PX,
} from "@/lib/freeze-visual-viewport";
import { isTextInput, isTextInputFocused } from "@/lib/is-text-input";
import { syncVisualViewportCssProperties } from "@/lib/sync-visual-viewport-css";

/**
 * Tracks `window.visualViewport` and sets CSS custom properties on `<html>`
 * for keyboard-aware layouts.
 *
 * While the keyboard is open, metrics freeze after a short settle delay so
 * field-to-field focus does not resize the pro dashboard shell. On dismiss,
 * viewport listeners and `focusout` always run a fresh sync (never skip while
 * frozen) and closed metrics are normalized for iOS `offsetTop` stuck bugs.
 */
const KEYBOARD_SYNC_RAF_FRAMES = 12;
const KEYBOARD_CLOSE_FOCUSOUT_MS = 100;

function useVisualViewport() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let previousKeyboardOffset = 0;
    let rafBurstId = 0;

    function update() {
      const snapshot = syncVisualViewportCssProperties();
      if (!snapshot) return;

      const keyboardClosed =
        previousKeyboardOffset > KEYBOARD_OPEN_OFFSET_PX &&
        snapshot.keyboardOffsetPx < KEYBOARD_CLOSE_OFFSET_PX;
      if (keyboardClosed && !isTextInputFocused()) {
        window.scrollTo(0, 0);
      }
      previousKeyboardOffset = snapshot.keyboardOffsetPx;
    }

    function runRafBurst() {
      const burstId = ++rafBurstId;
      let frame = 0;

      const tick = () => {
        if (burstId !== rafBurstId) return;
        update();
        frame += 1;
        if (frame < KEYBOARD_SYNC_RAF_FRAMES) {
          requestAnimationFrame(tick);
        }
      };

      requestAnimationFrame(tick);
    }

    function onViewportChange() {
      update();
    }

    function onFocusIn() {
      update();
      if (previousKeyboardOffset <= KEYBOARD_OPEN_OFFSET_PX) {
        runRafBurst();
      }
    }

    function onFocusOut(e: FocusEvent) {
      const target = e.target;
      if (!(target instanceof Element) || !isTextInput(target)) return;

      window.setTimeout(() => {
        if (isTextInputFocused()) return;
        clearVisualViewportFreeze();
        update();
        runRafBurst();
      }, KEYBOARD_CLOSE_FOCUSOUT_MS);
    }

    update();
    vv.addEventListener("resize", onViewportChange);
    vv.addEventListener("scroll", onViewportChange);
    document.addEventListener("focusin", onFocusIn, { passive: true });
    document.addEventListener("focusout", onFocusOut, { passive: true });

    return () => {
      rafBurstId += 1;
      vv.removeEventListener("resize", onViewportChange);
      vv.removeEventListener("scroll", onViewportChange);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
}

export { useVisualViewport };
